import * as React from "react";

import { Box } from "../../components/ui/box";
import { Input } from "../../components/ui/input";
import { Text, textVariants, type TextProps } from "../../components/ui/text";
import { Textarea } from "../../components/ui/textarea";
import { useMenuFocusStealGuard } from "../../hooks/use-menu-focus-steal-guard";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type EditableTitleFieldProps = Omittable<{
  /** Current committed title, rendered as plain text at rest. */
  value: string;
  /** Persist the trimmed next value when the edit commits (Enter/blur). */
  onCommit: (next: string) => void;
  size?: TextProps["size"];
  weight?: TextProps["weight"];
  align?: TextProps["align"];
  /** Span the available width (card header, pushing controls out); omit to
   *  hug the text (dialog breadcrumb / heading). */
  fill?: boolean;
  /** Pull the field left by its horizontal hover padding so the title text
   *  aligns flush with the content below it, while the hover/edit outline
   *  still bleeds the same 4px outward. Used in the card header. */
  flushLeft?: boolean;
  /** When false, renders plain non-interactive text with no edit affordance. */
  editable?: boolean;
  /** When false, the field draws no hover or editing outline — for a host
   *  whose own chrome already frames the edit (a card title renamed in
   *  place), where the caret and focus are the editing signal. The border is
   *  dropped, not merely transparent: such a host renders a bare Text at
   *  rest, so a reserved 1px here would shift the glyphs on entry. */
  outline?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  /** Controlled editing, for a host whose MENU launches the edit (a card
   *  menu's Rename): the host owns when editing is on, and the field guards
   *  its first blur against the closing menu's own focus traffic. Omit for
   *  the normal click-to-edit field. */
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  /** Edit as a wrapping block instead of a one-line input: the editor keeps
   *  the resting text's line boxes (a wrapped title stays wrapped, the caret
   *  lands in flowed text) and auto-grows with its content. Enter still
   *  commits — a title holds no newline. For hosts whose resting text wraps
   *  (a card title on a narrow column). */
  multiline?: boolean;
}>;

/**
 * An inline-editable title: plain text at rest that reveals a rounded
 * input-style outline on hover, and keeps that outline lit while it's being
 * edited (even after the pointer leaves) so the spot being edited stays
 * obvious. The border colors in from a permanently-reserved transparent 1px,
 * so nothing reflows. Enter/blur commit the trimmed value; Escape cancels.
 *
 * Used by widget cards and detail dialogs so every editable title reads the
 * same; `fill` adapts it to a full-width header vs. a content-hugging title.
 */
export function EditableTitleField({
  value,
  onCommit,
  size = "sm",
  weight = "semibold",
  align,
  fill,
  flushLeft,
  editable = true,
  outline = true,
  placeholder = "Untitled",
  "aria-label": ariaLabel,
  editing: controlledEditing,
  onEditingChange,
  multiline,
}: EditableTitleFieldProps) {
  const [uncontrolledEditing, setUncontrolledEditing] = React.useState(false);
  const isEditingControlled = controlledEditing !== undefined;
  const editing = isEditingControlled ? controlledEditing : uncontrolledEditing;
  const [draft, setDraft] = React.useState(value);
  const didSubmitRef = React.useRef(false);
  // Controlled editing was launched from outside (a menu's Rename), so its
  // first blur can be the closing menu's own focus traffic.
  const reclaimFromMenuSteal = useMenuFocusStealGuard(
    isEditingControlled && editing,
  );

  // The controlled field mounts already editing, so the draft seeds at
  // mount; the value effect below keeps it honest when editing is off.
  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const setEditing = (nextEditing: boolean) => {
    if (!isEditingControlled) setUncontrolledEditing(nextEditing);
    onEditingChange?.(nextEditing);
  };

  const startEditing = () => {
    if (!editable) return;
    didSubmitRef.current = false;
    setDraft(value);
    setEditing(true);
  };

  const finishEditing = (next: string) => {
    setEditing(false);
    const trimmed = next.trim();
    if (trimmed !== value) onCommit(trimmed);
  };

  const cancelEditing = () => {
    setDraft(value);
    setEditing(false);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (event.key === "Escape") cancelEditing();
    if (event.key === "Enter") {
      // A title holds no newline — Enter commits in the multiline editor too.
      event.preventDefault();
      didSubmitRef.current = true;
      finishEditing(draft);
    }
  };

  return (
    // Flex rows, never blocks: `truncate` is overflow + ellipsis, and an
    // inline span ignores both — the glyphs paint straight past the box. As a
    // flex item the Text span blockifies (and its overflow lifts the automatic
    // minimum size), so a long title clips inside the field instead of running
    // under whatever sits to its right.
    <Box minWidth={0} direction="row" align="center" rounded="md" paddingX="xs" paddingY="2xs"
      className={cn(
        // Outlined, the 1px border is reserved (transparent) in every state
        // so hover and editing recolor without reflow; outline-less hosts
        // render a bare Text at rest, so the border is dropped entirely to
        // keep the glyphs at the resting x.
        outline && "border border-transparent",
        fill && "w-full flex-1",
        flushLeft && "-ml-1",
        outline && editable && !editing && "hover:border-input",
        outline && editing && "border-ring",
      )}
    >
      {editing && multiline ? (
        // The wrapping editor: a bare Textarea wearing the SAME text classes
        // as the resting Text, so the wrapped title keeps its line boxes and
        // the caret lands in flowed text. `field-sizing-content` grows it
        // with the draft (the primitive's own sizing mechanism); Enter
        // commits via handleKeyDown, so no newline ever lands.
        <Textarea
          bare
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setDraft(event.target.value)
          }
          onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
            if (reclaimFromMenuSteal(event)) return;
            if (!didSubmitRef.current) finishEditing(draft);
          }}
          onKeyDown={handleKeyDown}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          onFocus={(event: React.FocusEvent<HTMLTextAreaElement>) => {
            // A textarea autofocuses with the caret at the START; the rename
            // gesture expects it at the end, where typing extends the title.
            const node = event.currentTarget;
            node.setSelectionRange(node.value.length, node.value.length);
          }}
          placeholder={placeholder}
          autoFocus
          rows={1}
          className={cn(
            textVariants({ align, size, weight }),
            "field-sizing-content w-full min-w-0 resize-none bg-transparent text-foreground outline-none placeholder:text-muted-foreground/60",
          )}
          aria-label={ariaLabel ?? "Edit title"}
        />
      ) : editing ? (
        // A bare Input wearing the SAME text classes as the resting Text (via
        // textVariants), so entering edit mode swaps the glyphs' owner without
        // changing their size, weight, or line box — the row never grows.
        // `bare` drops the primitive's shell and fixed metrics entirely.
        <Input
          bare
          value={draft}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            if (reclaimFromMenuSteal(event)) return;
            if (!didSubmitRef.current) finishEditing(draft);
          }}
          onKeyDown={handleKeyDown}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
          placeholder={placeholder}
          autoFocus
          className={cn(
            textVariants({ align, size, weight }),
            "w-full min-w-0 bg-transparent text-foreground outline-none placeholder:text-muted-foreground/60",
          )}
          aria-label={ariaLabel ?? "Edit title"}
        />
      ) : editable ? (
        <Box fill="width" minWidth={0} direction="row" align="center" cursor="text"
          as="button"
          className="text-left"
          onClick={startEditing}
          aria-label={ariaLabel ?? "Edit title"}
        >
          <Text size={size} weight={weight} align={align} truncate>
            {value.length > 0 ? value : placeholder}
          </Text>
        </Box>
      ) : (
        <Text size={size} weight={weight} align={align} truncate>
          {value}
        </Text>
      )}
    </Box>
  );
}
